import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCheckCircle,
  faBan,
  faHorseHead,
  faCreditCard,
  faExclamationTriangle,
  faUserInjured,
  faDollar,
  faDollarSign,
  faCircleDollarToSlot,
  faSackDollar,
  faSearchDollar,
  faCommentDollar,
  faCommentsDollar,
  faCheck,
  faHourglassHalf,
} from "@fortawesome/free-solid-svg-icons";
import { Ride } from "../../queries/models/ride.model";
import { useMemo } from "react";

export interface PlannedRideProps {
  ride: Ride;
  shortNames: boolean;
  showInstructor: boolean;
  onSelected: (ride: Ride) => void;
}

export function PlannedRide({
  ride,
  onSelected,
  shortNames,
  showInstructor,
}: PlannedRideProps) {
  const text = useMemo(() => {
    const parts = [];
    if (ride.rideType?.name) parts.push(ride.rideType.name);
    if (ride.rider?.name)
      parts.push(shortNames ? ride.rider.shortName : ride.rider.name);
    if (ride.horse?.name)
      parts.push(shortNames ? ride.horse.shortName : ride.horse.name);
    if (ride.user?.name && showInstructor)
      parts.push(shortNames ? ride.user.shortName : ride.user.username);
    return parts.join("-");
  }, [ride, shortNames, showInstructor]);

  const borderClass = useMemo(() => {
    if (ride.rider?.useCredits && ride.rider?.credits < 1) {
      return "border border-solid border-w-6 border-red-200";
    } else if (ride.isPaid) {
      return "border border-solid border-green-200";
    } else {
      return "";
    }
  }, [ride]);

  const textColorClass = useMemo(() => {
    if (ride.isHorseExhausted || ride.horseId == null) {
      return "text-blue-600";
    } else if (ride.isCancelled) {
      return "text-red-600";
    } else if (ride.hasIssue || ride.hasPreviousHorseIssues) {
      return "text-orange-500";
    } else if (ride.hasFall) {
      return "text-red-900";
    } else {
      return "text-normal-color";
    }
  }, [ride]);

  const icons: IconProp[] = useMemo(() => {
    const iconList: IconProp[] = [];
    if (ride.duration > 1) iconList.push(faHourglassHalf);
    if (ride.isPaid) iconList.push(faCheck);
    if (ride.isCancelled) iconList.push(faBan);
    if (ride.isHorseExhausted) iconList.push(faHorseHead);
    if (ride.rider?.useCredits && ride.rider?.credits < 1)
      iconList.push(faDollar);
    if (ride.hasIssue || ride.hasPreviousHorseIssues)
      iconList.push(faExclamationTriangle);
    if (ride.hasFall) iconList.push(faUserInjured);
    return iconList;
  }, [ride]);

  return (
    <div
      onClick={() => onSelected(ride)}
      style={{ background: ride.user?.color ?? "#ffffff" }}
      className={`shadow-1 m-1 p-2 flex align-items-center ${borderClass} ${textColorClass}`}
    >
      <div className="flex align-items-center mr-2">
        {icons.map((icon, idx) => (
          <FontAwesomeIcon
            key={idx}
            icon={icon}
            className="mr-1"
            style={{ verticalAlign: "middle" }}
          />
        ))}
      </div>
      <span className="flex align-items-center">{text}</span>
    </div>
  );
}
